import {
  ActionDropdownOverlayPosition,
  computeActionDropdownOverlayPosition,
} from './action-dropdown-position.util';

export interface ActionDropdownOverlaySyncOptions {
  anchor: HTMLElement;
  itemCount?: number;
  minWidth?: number;
  alignEnd?: boolean;
  onPosition: (position: ActionDropdownOverlayPosition) => void;
  /** When the anchor leaves the DOM (e.g. virtual scroll), close the menu. */
  onAnchorDetached?: () => void;
}

export interface ActionDropdownOverlaySyncHandle {
  reposition(): void;
  detach(): void;
}

/**
 * Keeps a fixed-position action dropdown aligned to its trigger while the user scrolls
 * the table (or any scrollable ancestor). Use one handle per open menu; call detach on close.
 */
export function attachActionDropdownOverlaySync(
  options: ActionDropdownOverlaySyncOptions,
): ActionDropdownOverlaySyncHandle {
  const update = (): void => {
    if (!document.contains(options.anchor)) {
      options.onAnchorDetached?.();
      return;
    }
    options.onPosition(
      computeActionDropdownOverlayPosition(options.anchor.getBoundingClientRect(), {
        itemCount: options.itemCount,
        minWidth: options.minWidth,
        alignEnd: options.alignEnd,
      }),
    );
  };

  const onScrollOrResize = (): void => update();

  update();
  window.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize);

  return {
    reposition: update,
    detach: () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    },
  };
}

/** Per-component helper: call open on toggle, close on dismiss. */
export class ActionDropdownOverlayController {
  private handle: ActionDropdownOverlaySyncHandle | null = null;

  open(options: ActionDropdownOverlaySyncOptions): void {
    this.close();
    this.handle = attachActionDropdownOverlaySync(options);
  }

  close(): void {
    this.handle?.detach();
    this.handle = null;
  }

  isActive(): boolean {
    return this.handle !== null;
  }
}

export interface InlineActionDropdownMenuSyncOptions {
  anchor: HTMLElement;
  resolveDropdown: () => HTMLElement | null;
  gap?: number;
  minWidth?: number;
  /** When true, menu width is `max-content` (still respects minWidth). */
  useMaxContentWidth?: boolean;
  /** Align menu to the trigger's right edge (Action column). */
  alignEnd?: boolean;
  onAnchorDetached?: () => void;
}

/** Keeps an in-DOM `.action-dropdown-menu` aligned when using `position: fixed`. */
export function attachInlineActionDropdownMenuSync(
  options: InlineActionDropdownMenuSyncOptions,
): ActionDropdownOverlaySyncHandle {
  const gap = options.gap ?? 4;

  const update = (): void => {
    if (!document.contains(options.anchor)) {
      options.onAnchorDetached?.();
      return;
    }
    const dropdown = options.resolveDropdown();
    if (!dropdown) {
      return;
    }
    const rect = options.anchor.getBoundingClientRect();
    const useMaxContent = options.useMaxContentWidth !== false;
    const minWidth = Math.max(rect.width, options.minWidth ?? 0);
    dropdown.style.position = 'fixed';
    dropdown.style.top = `${rect.bottom + gap}px`;
    if (options.alignEnd) {
      dropdown.style.left = 'auto';
      dropdown.style.right = `${window.innerWidth - rect.right}px`;
    } else {
      dropdown.style.left = `${rect.left}px`;
      dropdown.style.right = 'auto';
    }
    if (useMaxContent) {
      dropdown.style.width = 'max-content';
      dropdown.style.minWidth = 'max-content';
    } else {
      dropdown.style.width = `${minWidth}px`;
      dropdown.style.minWidth = `${minWidth}px`;
    }
    dropdown.style.display = 'block';
    dropdown.style.visibility = 'visible';
    dropdown.style.opacity = '1';
    dropdown.style.zIndex = '10000';
  };

  const onScrollOrResize = (): void => update();

  update();
  window.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize);

  return {
    reposition: update,
    detach: () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    },
  };
}

export class InlineActionDropdownMenuController {
  private handle: ActionDropdownOverlaySyncHandle | null = null;

  open(options: InlineActionDropdownMenuSyncOptions): void {
    this.close();
    this.handle = attachInlineActionDropdownMenuSync(options);
  }

  close(): void {
    this.handle?.detach();
    this.handle = null;
  }
}

/** Find the menu for an action button (wrapper first; optional attribute fallback). */
export function resolveInlineActionDropdownMenu(
  anchor: HTMLElement,
  rowId?: string,
  dataAttribute = 'data-dropdown-id',
): HTMLElement | null {
  const wrapper = anchor.closest('.action-cell-wrapper');
  const inWrapper = wrapper?.querySelector('.action-dropdown-menu');
  if (inWrapper instanceof HTMLElement) {
    return inWrapper;
  }
  if (!rowId) {
    return null;
  }
  try {
    const selector = `[${dataAttribute}="${CSS.escape(rowId)}"]`;
    return document.querySelector(selector) as HTMLElement | null;
  } catch {
    return null;
  }
}

/** Opens scroll-synced positioning for in-row `.action-dropdown-menu` elements. */
export function openInlineTableActionDropdown(options: {
  controller: InlineActionDropdownMenuController;
  event: Event;
  rowId: string;
  dataAttribute?: string;
  renderDelayMs?: number;
  minWidth?: number;
  /** @default true */
  useMaxContentWidth?: boolean;
  alignEnd?: boolean;
  onAnchorDetached: () => void;
}): void {
  const button =
    (options.event.currentTarget as HTMLElement | null) ??
    ((options.event.target as HTMLElement).closest('.action-btn') as HTMLElement | null);
  if (!button) {
    return;
  }
  const attr = options.dataAttribute ?? 'data-dropdown-id';
  const bind = (): void => {
    options.controller.open({
      anchor: button,
      minWidth: options.minWidth,
      useMaxContentWidth: options.useMaxContentWidth !== false,
      alignEnd: options.alignEnd,
      resolveDropdown: () =>
        resolveInlineActionDropdownMenu(button, options.rowId, attr),
      onAnchorDetached: options.onAnchorDetached,
    });
  };
  const delay = options.renderDelayMs ?? 0;
  if (delay > 0) {
    setTimeout(bind, delay);
  } else {
    bind();
  }
}
